import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { FaBook, FaClock, FaRupeeSign, FaArrowRight, FaStar, FaFire, FaChartLine } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import CourseService from '../../services/courseService';
import StudentService from '../../services/studentService';
import ContactModal from '../../components/common/ContactModal';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import '../../styles/AvailableCourses.css';

const AvailableCourses = () => {
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    loadAvailableCourses();
  }, []);

  const loadAvailableCourses = async () => {
    try {
      setLoading(true);
      
      // Get enrolled courses first
      const enrolledResponse = await StudentService.getStudentCourses(user.id);
      const enrolledIds = (enrolledResponse.data || []).map((c) => c.course_id);
      
      // Get all courses
      const allCoursesResponse = await CourseService.getAllCourses();
      const allCourses = allCoursesResponse.data || [];
      
      // Filter out enrolled courses
      const available = allCourses.filter((c) => !enrolledIds.includes(c.id));
      setAvailableCourses(available);
    } catch (error) {
      toast.error("Failed to load courses");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCourse = (courseId) => {
    window.open(`/courses/${courseId}`, '_blank');
  };

  const handleRequestEnrollment = (course) => {
    setSelectedCourse(course);
    setShowContactModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="available-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading available courses...</p>
        </div>
      </div>
    );
  }

  if (availableCourses.length === 0) {
    return (
      <div>
        <Header />
        <div className="available-empty">
          <FaBook size={60} className="empty-icon" />
          <h5>No Available Courses</h5>
          <p className="text-muted">You are enrolled in all available courses!</p>
          <Button variant="primary" onClick={() => window.location.href = '/student'}>
            Go to Dashboard <FaArrowRight className="ms-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="available-courses-modern">
        
        {/* Hero Section */}
        <section className="available-hero">
          <Container>
            <div className="hero-content">
              <h1 className="hero-title">
                Discover New Courses
                <span className="hero-emoji">🚀</span>
              </h1>
              <p className="hero-subtitle">
                Expand your skills with our industry-aligned courses
              </p>
            </div>
          </Container>
          <div className="hero-wave">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
            </svg>
          </div>
        </section>

        <Container className="available-container">
          {/* Stats Row */}
          <Row className="stats-row mb-5">
            <Col md={4} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-primary-soft">
                  <FaBook />
                </div>
                <div className="stat-info">
                  <h3>{availableCourses.length}</h3>
                  <p>Available Courses</p>
                </div>
              </div>
            </Col>
            <Col md={4} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-warning-soft">
                  <FaStar />
                </div>
                <div className="stat-info">
                  <h3>Industry Ready</h3>
                  <p>Job-Oriented Curriculum</p>
                </div>
              </div>
            </Col>
            <Col md={4} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-success-soft">
                  <FaFire />
                </div>
                <div className="stat-info">
                  <h3>Expert Mentors</h3>
                  <p>Learn from Professionals</p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Section Header */}
          <div className="section-header mb-4">
            <h2 className="section-title">
              <FaStar className="me-2 text-warning" />
              Courses Available for Enrollment
            </h2>
            <p className="section-subtitle">
              Choose the right course to boost your career
            </p>
          </div>

          {/* Courses Grid */}
          <Row className="courses-grid">
            {availableCourses.map((course) => (
              <Col lg={4} md={6} key={course.id} className="mb-4">
                <Card className="available-course-card h-100">
                  {course.image_url && (
                    <Card.Img 
                      variant="top" 
                      src={course.image_url} 
                      className="course-card-img"
                    />
                  )}
                  <Card.Body>
                    <div className="course-header">
                      <Badge bg="primary" className="course-badge">Available</Badge>
                      <h5 className="course-title">{course.name}</h5>
                      <p className="course-description">
                        {course.description?.substring(0, 100)}...
                      </p>
                    </div>

                    <div className="course-meta">
                      <div className="meta-item">
                        <FaClock className="meta-icon" />
                        <span>{course.duration}</span>
                      </div>
                      <div className="meta-item">
                        <FaRupeeSign className="meta-icon" />
                        <span>{formatCurrency(course.price)}</span>
                      </div>
                    </div>

                    <div className="course-actions">
                      <Button
                        variant="outline-info"
                        className="action-btn view-btn"
                        onClick={() => handleViewCourse(course.id)}
                      >
                        <FaBook className="me-2" /> View Details
                      </Button>
                      <Button
                        variant="primary"
                        className="action-btn enroll-btn"
                        onClick={() => handleRequestEnrollment(course)}
                      >
                        Request Enrollment <FaArrowRight className="ms-2" />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>

        {/* Contact Modal for Enrollment Request */}
        <ContactModal
          show={showContactModal}
          onHide={() => setShowContactModal(false)}
          courseName={selectedCourse?.name}
          courseId={selectedCourse?.id}
        />
      </div>
    </div>
  );
};

export default AvailableCourses;